import type { AdvanceTargetStatus, OrderResponse, OrderStatus } from '../types';

export interface LifecycleAction {
  labelKey: string;
  target: AdvanceTargetStatus;
}

/**
 * Picks the next advance action for an order based on its current status and
 * orderType. Returns `null` when the order is in a state that cannot be
 * advanced (PENDING uses accept/reject, and COMPLETED / CANCELLED are terminal).
 */
export function getNextLifecycleAction(order: OrderResponse): LifecycleAction | null {
  switch (order.orderStatus) {
    case 'CONFIRMED':
      return { labelKey: 'orders.action.startPreparing', target: 'PREPARING' };
    case 'PREPARING':
      switch (order.orderType) {
        case 'DELIVERY':
          return { labelKey: 'orders.action.dispatchDelivery', target: 'IN_DELIVERY' };
        case 'PICKUP':
          return { labelKey: 'orders.action.readyPickup', target: 'READY_FOR_PICKUP' };
        case 'DINE_IN':
          return { labelKey: 'orders.action.markCompleted', target: 'COMPLETED' };
        default:
          return null;
      }
    case 'IN_DELIVERY':
    case 'READY_FOR_PICKUP':
      return { labelKey: 'orders.action.markCompleted', target: 'COMPLETED' };
    // SCHEDULED, PENDING, COMPLETED, CANCELLED have no advance action
    default:
      return null;
  }
}

const TERMINAL_STATUSES: OrderStatus[] = ['COMPLETED', 'CANCELLED'];

export function isTerminalStatus(status: OrderStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

/**
 * Timestamp at which the order entered its current status. Falls through to
 * earlier lifecycle timestamps (and finally `createdAt`) when the expected
 * field is missing from legacy rows.
 */
export function stateEnteredAtIso(order: OrderResponse): string | undefined {
  switch (order.orderStatus) {
    case 'CANCELLED':
      return order.cancelledAt ?? order.updatedAt ?? order.createdAt;
    case 'COMPLETED':
      return order.completedAt ?? order.updatedAt ?? order.createdAt;
    case 'IN_DELIVERY':
      return (
        order.inDeliveryAt ??
        order.preparingAt ??
        order.confirmedAt ??
        order.createdAt
      );
    case 'READY_FOR_PICKUP':
      return (
        order.readyForPickupAt ??
        order.preparingAt ??
        order.confirmedAt ??
        order.createdAt
      );
    case 'PREPARING':
      return order.preparingAt ?? order.confirmedAt ?? order.createdAt;
    case 'CONFIRMED':
      return order.confirmedAt ?? order.createdAt;
    case 'SCHEDULED':
      return order.createdAt;
    case 'PENDING':
    default:
      return order.createdAt;
  }
}

/**
 * Comparator that puts the order which entered its current status earliest
 * at the front — oldest-waiting first.
 */
export function compareByStateEnteredAsc(a: OrderResponse, b: OrderResponse): number {
  return toMs(stateEnteredAtIso(a)) - toMs(stateEnteredAtIso(b));
}

function toMs(iso: string | undefined): number {
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}
